import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, TrendingUp, Loader2 } from 'lucide-react';
import { track } from '../lib/analytics';

const NICHES = [
  'Coach',
  'Course creator',
  'E-commerce',
  'SaaS founder',
  'Podcaster / creator',
  'Agency',
  'Newsletter operator',
  'Other',
];
const PLATFORMS = [
  'Kit.com',
  'ActiveCampaign',
  'Mailchimp',
  'Beehiiv',
  'Substack',
  'GoHighLevel',
  'MailerLite',
  'Klaviyo',
  'None yet',
  'Other',
];
const LIST_SIZES = [
  'Under 1,000',
  '1,000+',
  '5,000+',
  '10,000+',
  '50,000+',
  '100,000+',
];
const TIMELINES = [
  { value: 'This month', body: 'I want to start now.' },
  { value: 'Next 30-60 days', body: 'Planning ahead.' },
  { value: 'Just exploring', body: 'Curious about a fit.' },
];

const initialData = {
  firstName: '',
  email: '',
  location: '',
  niche: '',
  platform: '',
  listSize: '',
  working: '',
  fix: '',
  timeline: '',
  honeypot: '',
};

const AuditModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialData);
  const [startTs, setStartTs] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [done, setDone] = useState(false);
  const firstFieldRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setData(initialData);
      setStartTs(Date.now());
      setSubmitting(false);
      setStatus({ type: null, message: '' });
      setDone(false);
      setTimeout(() => firstFieldRef.current?.focus(), 60);
    }
  }, [isOpen]);

  // ESC + body lock
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setStatus({ type: null, message: '' });
  };

  const validateStep = () => {
    if (step === 1) {
      if (!data.firstName.trim()) return 'Please enter your first name.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return 'Please enter a valid email.';
      if (!data.location.trim()) return 'Where are you based?';
    }
    if (step === 2) {
      if (!data.niche) return 'Pick your niche.';
      if (!data.platform) return 'Pick your current platform.';
      if (!data.listSize) return 'Pick your list size range.';
    }
    if (step === 3) {
      if (data.working.trim().length < 5) return 'A one-line answer helps me a lot. What is working?';
      if (data.fix.trim().length < 5) return 'What is the #1 thing you would fix?';
      if (!data.timeline) return 'Pick a rough timeline.';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { setStatus({ type: 'error', message: err }); return; }
    setStatus({ type: null, message: '' });
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => {
    setStatus({ type: null, message: '' });
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    const err = validateStep();
    if (err) { setStatus({ type: 'error', message: err }); return; }
    if (data.honeypot) return;
    if (Date.now() - startTs < 3000) {
      setStatus({ type: 'error', message: 'Please take a moment to fill the form properly.' });
      return;
    }
    setSubmitting(true);
    setStatus({ type: null, message: '' });
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, elapsedMs: Date.now() - startTs }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not submit right now.');
      }
      setDone(true);
      track('audit_lead', {
        niche: data.niche || 'unknown',
        platform: data.platform || 'unknown',
        list_size: data.listSize || 'unknown',
      });
    } catch (e) {
      setStatus({ type: 'error', message: e.message || 'Could not submit right now.' });
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = done ? 100 : (step / 3) * 100;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="audit-modal-title"
      data-testid="audit-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-cream rounded-[1.75rem] max-w-xl w-full shadow-2xl relative animate-in border border-line overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="audit-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream-2 hover:bg-[var(--gh-orange)] hover:text-white text-ink flex items-center justify-center transition-colors z-10 border border-line"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header w/ progress bar */}
        <div className="bg-[var(--gh-orange)] text-[var(--gh-cream)] px-6 pt-6 pb-5 pr-14">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-90">
              {done ? 'Received' : `Free audit · Step ${step} of 3`}
            </span>
          </div>
          <h2 id="audit-modal-title" className="font-display text-2xl md:text-[26px] font-extrabold leading-tight">
            {done
              ? 'Brief received. Watch your inbox.'
              : step === 1
              ? "Let's start with you."
              : step === 2
              ? 'Tell me about your business.'
              : 'What are we optimizing?'}
          </h2>
          {!done && (
            <div className="mt-4 h-1.5 rounded-full bg-white/25 overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-300"
                style={{ width: `${progressPct}%` }}
                data-testid="audit-progress"
              />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {done ? (
            <div className="text-center py-6" data-testid="audit-success">
              <div className="w-14 h-14 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center mx-auto">
                <CheckCircle className="w-7 h-7" />
              </div>
              <p className="mt-4 font-display text-xl font-bold text-ink">Got it, {data.firstName}.</p>
              <p className="mt-2 text-sm text-muted-warm max-w-sm mx-auto leading-relaxed">
                I read every audit brief personally. Within 24 hours you will get a reply with three specific things I would ship in week one.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                <a
                  href="https://tidycal.com/akashtiwari/30-minute-meeting"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="audit-book-call"
                  className="btn-orange justify-center"
                >
                  Book a 30-min call <ArrowRight className="w-4 h-4" />
                </a>
                <button onClick={onClose} data-testid="audit-close-success" className="btn-outline justify-center">
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); step < 3 ? next() : submit(); }} className="space-y-4" data-testid="audit-form">
              {step === 1 && (
                <>
                  <Field label="First name">
                    <input
                      ref={firstFieldRef}
                      type="text"
                      required
                      value={data.firstName}
                      onChange={(e) => set('firstName', e.target.value)}
                      placeholder="Akash"
                      data-testid="audit-first-name"
                      className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      required
                      value={data.email}
                      onChange={(e) => set('email', e.target.value)}
                      placeholder="you@brand.com"
                      data-testid="audit-email"
                      className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </Field>
                  <Field label="Where are you based?">
                    <input
                      type="text"
                      required
                      value={data.location}
                      onChange={(e) => set('location', e.target.value)}
                      placeholder="City, country (e.g. Bangalore, India)"
                      data-testid="audit-location"
                      className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </Field>
                </>
              )}

              {step === 2 && (
                <>
                  <Field label="Your niche">
                    <Select value={data.niche} onChange={(v) => set('niche', v)} options={NICHES} placeholder="Choose your niche" testid="audit-niche" />
                  </Field>
                  <Field label="Current email platform">
                    <Select value={data.platform} onChange={(v) => set('platform', v)} options={PLATFORMS} placeholder="Choose your platform" testid="audit-platform" />
                  </Field>
                  <Field label="List size">
                    <div className="grid grid-cols-3 gap-2" data-testid="audit-list-size">
                      {LIST_SIZES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => set('listSize', s)}
                          data-testid={`audit-list-size-${s.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                          className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                            data.listSize === s
                              ? 'bg-[var(--gh-orange)] text-white border-transparent'
                              : 'bg-cream-2 text-ink border-line hover:border-ink'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>
                </>
              )}

              {step === 3 && (
                <>
                  <Field label="What's working right now?">
                    <textarea
                      required
                      rows={3}
                      value={data.working}
                      onChange={(e) => set('working', e.target.value)}
                      placeholder="Best-performing campaign, favorite automation, biggest win…"
                      data-testid="audit-working"
                      className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all resize-none text-ink"
                    />
                  </Field>
                  <Field label="What's the #1 thing you'd fix?">
                    <textarea
                      required
                      rows={3}
                      value={data.fix}
                      onChange={(e) => set('fix', e.target.value)}
                      placeholder="Low opens, dead segments, broken deliverability, no monetization…"
                      data-testid="audit-fix"
                      className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all resize-none text-ink"
                    />
                  </Field>
                  <Field label="How soon do you want to start?">
                    <div className="grid gap-2" data-testid="audit-timeline">
                      {TIMELINES.map((t) => {
                        const active = data.timeline === t.value;
                        return (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => set('timeline', t.value)}
                            data-testid={`audit-timeline-${t.value.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                            className={`text-left px-4 py-3 rounded-2xl border transition-colors ${
                              active
                                ? 'bg-[var(--gh-orange-soft)] border-[var(--gh-orange)] text-ink'
                                : 'bg-cream-2 border-line hover:border-ink text-ink'
                            }`}
                          >
                            <div className="font-semibold text-sm">{t.value}</div>
                            <div className="text-xs text-muted-warm mt-0.5">{t.body}</div>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </>
              )}

              {/* honeypot */}
              <input
                type="text"
                name="company"
                value={data.honeypot}
                onChange={(e) => set('honeypot', e.target.value)}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
              />

              {status.type === 'error' && (
                <p className="text-sm text-red-600 -mt-1" data-testid="audit-error">{status.message}</p>
              )}

              <div className="flex items-center justify-between pt-2">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={back}
                    data-testid="audit-back"
                    className="btn-outline"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                ) : <span />}

                {step < 3 ? (
                  <button
                    type="submit"
                    data-testid="audit-next"
                    className="btn-orange"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="audit-submit-btn"
                    className="btn-orange disabled:opacity-60"
                  >
                    {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>)
                      : (<>Send my audit brief <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {!done && (
          <div className="px-6 pb-5 pt-2 text-[11px] text-muted-warm text-center border-t border-line">
            No commitment. I read every brief and reply within 24 hours.
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all appearance-none pr-10 ${
        value ? 'text-ink' : 'text-muted-warm'
      }`}
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <svg
      className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 text-muted-warm"
      viewBox="0 0 20 20" fill="currentColor"
    >
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  </div>
);

export default AuditModal;
